"use client";

import { useState } from "react";
import Link from "next/link";
import { Bot, Search, TrendingUp } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { AuditReportPreview } from "~/components/siteglue/home/home-hero-report-preview";
import {
  marketingCardClass,
  marketingCardIconClass,
  marketingVerticalTabClass,
} from "~/components/siteglue/home/marketing-surface";
import { FadeIn, HomeEyebrow, HomeHeading, HomeLead, HomeSection } from "~/components/siteglue/home/section-shell";
import { AUDIT_SERVICE_PAGES } from "~/lib/siteglue/audits/catalog";
import {
  HOMEPAGE_AUDITS_EYEBROW,
  HOMEPAGE_AUDITS_HEADING,
} from "~/lib/siteglue/landing-redesign/copy";
import {
  homepagePreviewSlide,
  OPTIMIZATION_SERVICE_TAB_ORDER,
  type HomepagePreviewSlideId,
} from "~/lib/siteglue/landing-redesign/homepage-hero";
import { PUBLIC_PRICING_CARDS } from "~/lib/siteglue/public-pricing";

const TAB_PAGE_SLUG: Record<HomepagePreviewSlideId, string> = {
  aro: "agent-readiness",
  aeo: "answer-engine-optimization",
  cro: "conversion-optimization",
};

const TAB_LABELS: Record<
  HomepagePreviewSlideId,
  { short: string; full: string; icon: typeof Bot }
> = {
  aro: { short: "ARO", full: "Agent Readiness", icon: Bot },
  aeo: { short: "AEO", full: "Answer Engine Optimization", icon: Search },
  cro: { short: "CRO", full: "Conversion Optimization", icon: TrendingUp },
};

const MAX_BENCHMARKS = 6;

export function HomeOptimizationServices() {
  const [active, setActive] = useState<HomepagePreviewSlideId>("aro");
  const page = AUDIT_SERVICE_PAGES.find((item) => item.slug === TAB_PAGE_SLUG[active]);
  const pricing = PUBLIC_PRICING_CARDS.find((card) => card.id === active);
  const slide = homepagePreviewSlide(active);
  const benchmarks = page?.benchmarks?.slice(0, MAX_BENCHMARKS) ?? [];

  return (
    <HomeSection tone="white" className="py-20 sm:py-28">
      <FadeIn className="text-center">
        <HomeEyebrow className="justify-center">{HOMEPAGE_AUDITS_EYEBROW}</HomeEyebrow>
        <HomeHeading className="mx-auto">
          SiteGlue AI{" "}
          <span className="text-[#4f81bd]">
            connects people and their AI agents to the right person on your team.
          </span>
        </HomeHeading>
        <HomeLead className="mx-auto">
          Three free audits around one SiteGlue Agent — ARO, AEO, and CRO. Pick an audit to see
          what it measures, then run it on your website.
        </HomeLead>
      </FadeIn>
      <div className="mt-12 flex flex-col gap-6 md:flex-row">
        <div className="md:w-64 md:shrink-0">
          <div
            role="tablist"
            aria-label="Free AI optimization audits"
            aria-orientation="vertical"
            className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible"
          >
            {OPTIMIZATION_SERVICE_TAB_ORDER.map((id) => {
              const selected = active === id;
              const label = TAB_LABELS[id];
              const Icon = label.icon;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`optimization-tab-${id}`}
                  aria-selected={selected}
                  aria-controls={`optimization-panel-${id}`}
                  onClick={() => setActive(id)}
                  className={marketingVerticalTabClass(selected)}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">
                      {label.short}
                    </span>
                    <span className="block">{label.full}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          {page ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                id={`optimization-panel-${active}`}
                role="tabpanel"
                aria-labelledby={`optimization-tab-${active}`}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
                  {page.eyebrow}
                </p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">
                  {page.headline}
                </h3>
                {pricing ? (
                  <p className="mt-2 text-sm font-semibold text-slate-900">{pricing.priceLabel}</p>
                ) : null}
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{page.description}</p>
                {benchmarks.length > 0 ? (
                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {benchmarks.map((item) => (
                      <div key={item.title} className={marketingCardClass()}>
                        <div className="flex items-start gap-4">
                          <div className={marketingCardIconClass()} aria-hidden>
                            <span className="text-sm font-semibold">•</span>
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-slate-900">{item.title}</h4>
                            <p className="mt-1 text-sm leading-relaxed text-slate-500">{item.body}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <Link
                  href={page.href}
                  className="mt-5 inline-flex text-sm font-semibold text-[#4f81bd] hover:underline"
                >
                  {page.ctaLabel}
                </Link>
              </motion.div>
            </AnimatePresence>
          ) : null}
        </div>
      </div>
      {slide ? (
        <div className="mx-auto mt-10 max-w-3xl">
          <AuditReportPreview slide={slide} />
        </div>
      ) : null}
    </HomeSection>
  );
}
